import React, { useEffect, useState } from "react";
import { Plus, Search, X, Edit2, ListRestart, Copy, RadioTower } from "lucide-react";
import RANForm from "../components/forms/RanForm";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable";
import EmptyState from "../components/common/EmptyState";
import ChipList from "../components/common/ChipList";
import Pagination from "../components/common/Pagination";
import useAlert from "../components/hook/useAlert";
import callApi from "../helpers/networkHelper";
import API_CONFIG from "../config/apiConstant";

// CONSTANTS
const PAGE_SIZE = 10;

// HELPERS
const buildQueryString = (params) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== "") q.append(k, v);
    });
    return q.toString();
};

const getReadableErrorMessage = (error) => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return "Something went wrong. Please try again.";
};

const maskFingerprint = (fp) => {
    if (!fp || typeof fp !== "string") return "—";
    if (fp.length < 12) return fp;
    return `${fp.slice(0, 6)}••••••${fp.slice(-6)}`;
};

const copyToClipboard = async (text, showSuccess, showError) => {
    try {
        await navigator.clipboard.writeText(text);
        showSuccess("Hardware fingerprint copied");
    } catch {
        showError("Failed to copy hardware fingerprint");
    }
};

// MAIN COMPONENT
const RAN = () => {
    const { showSuccess, showError } = useAlert();

    const [rans, setRans] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [loadingPage, setLoadingPage] = useState(true);
    const [loadingTable, setLoadingTable] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRan, setEditingRan] = useState(null);

    const totalPages = Math.ceil(total / PAGE_SIZE);

    // FETCH
    const fetchRANs = async (initial = false) => {
        initial ? setLoadingPage(true) : setLoadingTable(true);

        try {
            const qs = buildQueryString({
                page,
                limit: PAGE_SIZE,
                search,
                isActive:
                    statusFilter === "all"
                        ? undefined
                        : statusFilter === "active"
            });

            const res = await callApi(
                API_CONFIG.RAN.GET.METHOD,
                `${API_CONFIG.RAN.GET.ENDPOINT}?${qs}`
            );

            setRans(res.data.data || []);
            setTotal(res.data.total || 0);
        } catch (e) {
            showError(getReadableErrorMessage(e));
        } finally {
            setLoadingPage(false);
            setLoadingTable(false);
        }
    };

    useEffect(() => {
        fetchRANs(true);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!loadingPage) fetchRANs(false);
        // eslint-disable-next-line
    }, [page, search, statusFilter]);

    // CREATE / UPDATE
    const handleSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            if (editingRan) {
                await callApi(
                    API_CONFIG.RAN.UPDATE.METHOD,
                    API_CONFIG.RAN.UPDATE.ENDPOINT.replace(":id", editingRan._id),
                    data
                );
                showSuccess("RAN updated successfully");
            } else {
                await callApi(
                    API_CONFIG.RAN.CREATE.METHOD,
                    API_CONFIG.RAN.CREATE.ENDPOINT,
                    data
                );
                showSuccess("RAN created successfully");
            }

            setIsModalOpen(false);
            setEditingRan(null);
            fetchRANs();
        } catch (e) {
            showError(getReadableErrorMessage(e));
        } finally {
            setIsSubmitting(false);
        }
    };

    // TOGGLE STATUS
    const toggleStatus = async (ran) => {
        try {
            await callApi(
                API_CONFIG.RAN.TOGGLE_STATUS.METHOD,
                API_CONFIG.RAN.TOGGLE_STATUS.ENDPOINT.replace(":id", ran._id),
                { isActive: !ran.isActive }
            );
            showSuccess("RAN status updated");
            fetchRANs();
        } catch (e) {
            showError(getReadableErrorMessage(e));
        }
    };

    // PAGE LOADER
    const hasActiveFilters = search.trim() !== "" || statusFilter !== "all";

    const clearFilters = () => {
        setSearch("");
        setStatusFilter("all");
        setPage(1);
    };

    const columns = [
        {
            key: "ranID",
            label: "RAN ID",
            nowrap: true,
            render: (ran) => (
                <span className="font-medium" style={{ color: "var(--brand)" }}>
                    {ran.ranID}
                </span>
            ),
        },
        {
            key: "hardwareFingerprint",
            label: "Hardware Fingerprint",
            nowrap: true,
            render: (ran) => (
                <div className="flex items-center gap-3">
                    <span className="font-mono" style={{ color: "var(--main-text-muted)" }}>
                        {maskFingerprint(ran.hardwareFingerprint)}
                    </span>
                    <button
                        onClick={() =>
                            copyToClipboard(ran.hardwareFingerprint, showSuccess, showError)
                        }
                        aria-label="Copy hardware fingerprint"
                        title="Copy hardware fingerprint"
                        className="hover:opacity-70"
                        style={{ color: "var(--main-text-muted)" }}
                    >
                        <Copy size={16} />
                    </button>
                </div>
            ),
        },
        {
            key: "features",
            label: "Features",
            render: (ran) => (
                <ChipList
                    items={ran.features || []}
                    max={4}
                    palette={{
                        bg: "var(--chip-feature-bg)",
                        text: "var(--chip-feature-text)",
                        border: "var(--chip-feature-border)",
                    }}
                />
            ),
        },
        {
            key: "status",
            label: "Status",
            nowrap: true,
            render: (ran) => (
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => toggleStatus(ran)}
                        role="switch"
                        aria-checked={ran.isActive}
                        aria-label={ran.isActive ? "Deactivate RAN" : "Activate RAN"}
                        title={ran.isActive ? "Deactivate RAN" : "Activate RAN"}
                        className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300"
                        style={{ background: ran.isActive ? "var(--success)" : "var(--danger)" }}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${
                                ran.isActive ? "translate-x-6" : "translate-x-1"
                            }`}
                        />
                    </button>
                    <span
                        className="text-xs font-medium min-w-[56px]"
                        style={{ color: ran.isActive ? "var(--success)" : "var(--danger)" }}
                    >
                        {ran.isActive ? "Active" : "Inactive"}
                    </span>
                </div>
            ),
        },
        {
            key: "actions",
            label: "Actions",
            nowrap: true,
            align: "right",
            render: (ran) => (
                <button
                    disabled={!ran.isActive}
                    aria-label={`Edit ${ran.ranID}`}
                    title={ran.isActive ? "Edit RAN" : "Activate the RAN to edit it"}
                    onClick={() => {
                        if (!ran.isActive) return;
                        setEditingRan(ran);
                        setIsModalOpen(true);
                    }}
                    className={ran.isActive ? "hover:opacity-80" : "opacity-40 cursor-not-allowed"}
                    style={{ color: "var(--brand)" }}
                >
                    <Edit2 size={16} />
                </button>
            ),
        },
    ];

    return (
        <div className="p-4 sm:p-6 min-h-screen w-full" style={{ background: "var(--main-bg)", color: "var(--main-text)" }}>

            <PageHeader
                icon={<RadioTower size={28} strokeWidth={2.2} />}
                title="RANs"
                subtitle="Manage Radio Access Networks"
                meta={loadingPage ? null : `${total} ${total === 1 ? "RAN" : "RANs"}`}
                actions={
                    <button
                        onClick={() => {
                            setEditingRan(null);
                            setIsModalOpen(true);
                        }}
                        className="flex items-center gap-2 font-medium px-4 h-10 rounded-lg transition hover:opacity-90"
                        style={{ background: "var(--brand)", color: "#fff" }}
                    >
                        <Plus size={18} />
                        Create RAN
                    </button>
                }
            />

            {/* FILTERS */}
            <div className="p-4 sm:p-5 rounded-lg mb-6 border" style={{ background: "var(--card-bg)", borderColor: "var(--card-border)" }}>
                <div className="flex flex-col sm:flex-row sm:items-end gap-4">

                    <div className="flex-1 min-w-0">
                        <label htmlFor="ran-search" className="text-sm mb-2 block" style={{ color: "var(--main-text)" }}>
                            Search
                        </label>
                        <div className="relative">
                            <Search size={18} className="absolute left-3 top-2.5 pointer-events-none" style={{ color: "var(--main-text-muted)" }} />
                            <input
                                id="ran-search"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full pl-10 pr-9 h-10 border rounded-lg"
                                style={{
                                    background: "var(--input-background)",
                                    color: "var(--main-text)",
                                    borderColor: "var(--card-border)",
                                }}
                                placeholder="Search by RAN ID or hardware fingerprint"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => { setSearch(""); setPage(1); }}
                                    aria-label="Clear search"
                                    className="absolute right-2 top-2.5 p-0.5 rounded hover:opacity-70"
                                    style={{ color: "var(--main-text-muted)" }}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="ran-status" className="text-sm mb-2 block" style={{ color: "var(--main-text)" }}>
                            Status
                        </label>
                        <select
                            id="ran-status"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            className="w-full sm:w-[140px] h-10 border rounded-lg px-3"
                            style={{
                                background: "var(--input-background)",
                                color: "var(--main-text)",
                                borderColor: "var(--card-border)",
                            }}
                        >
                            <option value="all">All</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    <button
                        onClick={clearFilters}
                        disabled={!hasActiveFilters}
                        className="flex items-center justify-center gap-2 h-10 px-4 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: "var(--brand-light)", color: "var(--brand)" }}
                    >
                        <ListRestart size={16} />
                        Reset
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                rows={rans}
                loading={loadingPage || loadingTable}
                getRowKey={(ran) => ran._id}
                emptyState={
                    hasActiveFilters ? (
                        <EmptyState
                            icon={<Search size={26} />}
                            title="No RANs match these filters"
                            description="Try a different search term, or clear the filters to see every RAN."
                            action={
                                <button
                                    onClick={clearFilters}
                                    className="h-10 px-4 rounded-lg font-medium border transition hover:opacity-80"
                                    style={{ color: "var(--brand)", borderColor: "var(--brand)" }}
                                >
                                    Clear filters
                                </button>
                            }
                        />
                    ) : (
                        <EmptyState
                            icon={<RadioTower size={26} />}
                            title="No RANs yet"
                            description="A RAN must be registered before a license can be issued against its hardware."
                            action={
                                <button
                                    onClick={() => {
                                        setEditingRan(null);
                                        setIsModalOpen(true);
                                    }}
                                    className="flex items-center gap-2 h-10 px-4 rounded-lg font-medium"
                                    style={{ background: "var(--brand)", color: "#fff" }}
                                >
                                    <Plus size={18} /> Create RAN
                                </button>
                            }
                        />
                    )
                }
            />

            <Pagination
                page={page}
                totalPages={totalPages}
                totalRecords={total}
                onPageChange={setPage}
            />

            {/* MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
                    <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-xl p-6">
                        <div className="flex justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white">
                                {editingRan ? "Edit RAN" : "Create RAN"}
                            </h3>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setEditingRan(null);
                                }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X />
                            </button>
                        </div>

                        <RANForm
                            onSubmit={handleSubmit}
                            isLoading={isSubmitting}
                            defaultValues={editingRan}
                            submitLabel={editingRan ? "Update RAN" : "Create RAN"}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default RAN;
